import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ApiFullTaxReportService } from '../../../../core/services/api/report/api-fulltax-report.service';
import { MapperFullTaxReportService } from '../../../../core/mappers/report/mapper-fulltax-report.service';
import { FullTaxReport, FullTaxReportDto, isFullTaxInvoiceEditable } from '../../../../core/models/report/fulltax-report.model';
import { ModalFullTaxReportEditComponent } from '../../modals/report/modal-full-tax-report-edit/modal-full-tax-report-edit.component';

// Custom Date Format สำหรับแสดงผล วว/ดด/ปป (DD/MM/YYYY)
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  }
};

@Component({
  selector: 'app-full-tax-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalFullTaxReportEditComponent
  ],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'th-TH' }
  ],
  templateUrl: './full-tax-report.component.html',
  styleUrl: './full-tax-report.component.css'
})
export class FullTaxReportComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiFullTaxReportService);
  private readonly mapper = inject(MapperFullTaxReportService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toast = inject(ToastService);
  private readonly dateAdapter = inject(DateAdapter);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  readonly isFullTaxInvoiceEditable = isFullTaxInvoiceEditable;

  // --- UI States ---
  isLoading = false;
  lastFetchedTime: Date | null = null;
  showTableFilters = false;

  // --- Data Lists ---
  invoices: FullTaxReport[] = [];
  rawInvoicesDto: FullTaxReportDto[] = [];

  // --- Search & Standard Filters ---
  searchText = '';
  searchType: 'customerName' | 'receiptId' | 'taxId' = 'customerName';
  isSearchTypeDropdownOpen = false;

  // --- Advanced Filters ---
  filterStatus: string | 'all' = 'all';
  startDate: Date | null = null;
  endDate: Date | null = null;
  isStatusDropdownOpen = false;

  // --- Custom Searchable Autocomplete Dropdown ---
  searchFieldText = '';
  selectedCustomerFilter = 'all';
  isCustomerDropdownOpen = false;
  customerOptions: string[] = [];

  // --- Pagination ---
  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [10, 20, 50, 100];
  totalItems = 0;
  totalPages = 0;
  isPageSizeDropdownOpen = false;

  // --- Sorting ---
  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' | '' = 'desc';

  // --- PDF Preview Modal ---
  isPdfPreviewOpen = false;
  previewingInvoice: FullTaxReport | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  // --- Edit Modal ---
  isEditModalOpen = false;
  editingInvoiceId: number | null = null;

  // --- Confirm Modal ---
  isConfirmModalOpen = false;
  confirmTarget: FullTaxReport | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage = 1;
        this.loadTableData();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  // ================= Normal Functions =================

  ngOnInit() {
    this.dateAdapter.setLocale('th-TH');
    this.setDefaultDates();
    if (this.selectedSiteId) {
      this.loadTableData();
    }
  }

  /**
   * Sets default date range to past 30 days
   */
  setDefaultDates() {
    const today = new Date();
    this.endDate = today;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.startDate = thirtyDaysAgo;
  }

  /**
   * Toggles advanced horizontal filters
   */
  toggleTableFilters() {
    this.showTableFilters = !this.showTableFilters;
  }

  /**
   * Handles search dropdown selection
   */
  toggleSearchTypeDropdown() {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'customerName' | 'receiptId' | 'taxId') {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  closeSearchTypeDropdown() {
    setTimeout(() => {
      this.isSearchTypeDropdownOpen = false;
    }, 200);
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'receiptId':
        return 'เลขที่ใบกำกับภาษี';
      case 'taxId':
        return 'เลขประจำตัวผู้เสียภาษี';
      default:
        return 'ชื่อลูกค้า';
    }
  }

  /**
   * Search and Filter trigger methods
   */
  onSearchChange() {
    this.currentPage = 1;
    this.loadTableData();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadTableData();
  }

  onDateChange() {
    this.currentPage = 1;
    this.loadTableData();
  }

  onRefresh() {
    this.searchText = '';
    this.searchType = 'customerName';
    this.filterStatus = 'all';
    this.selectedCustomerFilter = 'all';
    this.searchFieldText = '';
    this.setDefaultDates();
    this.currentPage = 1;
    this.loadTableData();
    this.toast.info('รีเฟรชข้อมูลเรียบร้อยแล้ว');
  }

  /**
   * Extract unique customer options for the advanced filter searchable combo box
   */
  extractFilterOptions(dtoList: FullTaxReportDto[]) {
    if (!dtoList) return;
    const names = dtoList.map(x => x.customerName).filter(Boolean);
    this.customerOptions = Array.from(new Set(names)).sort();
  }

  // --- Custom Dropdown Combo Box Logic ---

  get filteredOptions(): string[] {
    const query = this.searchFieldText.toLowerCase().trim();
    if (!query) return this.customerOptions;
    return this.customerOptions.filter(opt => opt.toLowerCase().includes(query));
  }

  toggleCustomerDropdown() {
    this.isCustomerDropdownOpen = !this.isCustomerDropdownOpen;
    if (this.isCustomerDropdownOpen && this.selectedCustomerFilter === 'all') {
      this.searchFieldText = '';
    }
  }

  selectCustomerOption(customer: string) {
    this.selectedCustomerFilter = customer;
    this.searchFieldText = customer;
    this.isCustomerDropdownOpen = false;
    this.onFilterChange();
  }

  clearCustomerSelection() {
    this.selectedCustomerFilter = 'all';
    this.searchFieldText = '';
    this.isCustomerDropdownOpen = false;
    this.onFilterChange();
  }

  closeDropdowns() {
    // Wait slightly for click event to complete
    setTimeout(() => {
      this.isCustomerDropdownOpen = false;
      // Strict validation: if text matches an option, save it. Otherwise, revert to last valid selected
      const match = this.customerOptions.find(opt => opt.toLowerCase() === this.searchFieldText.toLowerCase().trim());
      if (match) {
        this.selectedCustomerFilter = match;
        this.searchFieldText = match;
      } else {
        if (this.selectedCustomerFilter !== 'all') {
          this.searchFieldText = this.selectedCustomerFilter;
        } else {
          this.searchFieldText = '';
        }
      }
      this.onFilterChange();
    }, 200);
  }

  // --- Custom Status Dropdown Logic ---

  toggleStatusDropdown() {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
  }

  selectStatus(status: string) {
    this.filterStatus = status;
    this.isStatusDropdownOpen = false;
    this.onFilterChange();
  }

  closeStatusDropdown() {
    setTimeout(() => {
      this.isStatusDropdownOpen = false;
    }, 200);
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case '0':
        return 'รอดำเนินการ';
      case '1':
        return 'ยืนยันแล้ว';
      case '2':
        return 'ยกเลิก';
      default:
        return 'ทั้งหมด';
    }
  }

  // --- Custom Page Size Dropdown Logic ---

  togglePageSizeDropdown() {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  selectPageSize(size: number) {
    this.pageSize = size;
    this.isPageSizeDropdownOpen = false;
    this.onPageSizeChange();
  }

  closePageSizeDropdown() {
    setTimeout(() => {
      this.isPageSizeDropdownOpen = false;
    }, 200);
  }

  // --- Table Sorting & Pagination ---

  sortData(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadTableData();
  }

  setPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTableData();
    }
  }

  onPageSizeChange() {
    this.currentPage = 1;
    this.loadTableData();
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray() {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  // --- UI Presentation and Status Colors ---

  getStatusConfig(status: string) {
    switch (status) {
      case '0':
        return {
          label: 'รอดำเนินการ',
          class: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200/50 dark:border-amber-800',
          icon: 'pending'
        };
      case '1':
        return {
          label: 'ยืนยันแล้ว',
          class: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-800',
          icon: 'verified'
        };
      case '2':
        return {
          label: 'ยกเลิก',
          class: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-rose-200/50 dark:border-rose-800',
          icon: 'cancel'
        };
      default:
        return {
          label: 'ไม่ระบุ',
          class: 'bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200/50 dark:border-slate-600',
          icon: 'help_outline'
        };
    }
  }

  formatDateDisplay(date: Date): string {
    if (!date) return '-';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear() + 543; // Buddhist Era
    return `${day}/${month}/${year}`;
  }

  // --- PDF Viewer controls ---

  closePdfPreview() {
    this.isPdfPreviewOpen = false;
    this.previewingInvoice = null;
    if (this.pdfUrl) {
      // Clean up object URL memory leak
      URL.revokeObjectURL(this.pdfUrl.toString());
      this.pdfUrl = null;
    }
  }

  // --- Edit Modal Controls ---
  openEditModal(item: FullTaxReport) {
    if (!isFullTaxInvoiceEditable(item.status)) {
      this.toast.error('ใบกำกับภาษีนี้ยืนยันแล้ว ไม่สามารถแก้ไขได้');
      return;
    }
    this.editingInvoiceId = item.id;
    this.isEditModalOpen = true;
  }

  closeEditModal() {
    this.isEditModalOpen = false;
    this.editingInvoiceId = null;
  }

  onEditSuccess() {
    this.closeEditModal();
    this.loadTableData();
  }

  openConfirmModal(item: FullTaxReport) {
    if (!isFullTaxInvoiceEditable(item.status)) {
      this.toast.error('ใบกำกับภาษีนี้ยืนยันแล้ว');
      return;
    }
    this.confirmTarget = item;
    this.isConfirmModalOpen = true;
  }

  closeConfirmModal() {
    this.isConfirmModalOpen = false;
    this.confirmTarget = null;
  }

  confirmFullTaxInvoice() {
    const target = this.confirmTarget;
    if (!target) return;

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url);

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์ยืนยัน');
      return;
    }

    this.isLoading = true;
    this.apiService.confirmFullTaxInvoice({
      id: target.id,
      userId,
      menuId,
    }).subscribe({
      next: (res) => {
        const result = this.mapper.mapActionResponse(res);
        this.isLoading = false;
        this.closeConfirmModal();
        if (result.isSuccess) {
          this.toast.success('ยืนยันใบกำกับภาษีเรียบร้อยแล้ว');
          this.loadTableData();
          return;
        }
        this.toast.error(result.message || 'ยืนยันไม่สำเร็จ');
      },
      error: (err) => {
        this.isLoading = false;
        this.closeConfirmModal();
        const message = err?.error?.message ?? err?.error?.Message;
        this.toast.error(message || 'ยืนยันไม่สำเร็จ');
      }
    });
  }

  // ================= API Functions =================

  /**
   * Fetches data table records based on filters, pagination, and sorting
   */
  loadTableData() {
    const siteId = this.selectedSiteId;
    const userId = this.resolveUserId();

    if (!siteId || !userId) {
      this.invoices = [];
      this.totalItems = 0;
      this.totalPages = 0;
      return;
    }

    this.isLoading = true;

    // Map dates to query format
    const startStr = this.startDate ? this.startDate.toISOString().split('T')[0] : undefined;
    const endStr = this.endDate ? this.endDate.toISOString().split('T')[0] : undefined;

    this.apiService.getFullTaxInvoicesForTable({
      siteId,
      userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.mapper.mapSearchField(this.searchType),
      searchText: this.searchText,
      status: this.filterStatus,
      startDate: startStr,
      endDate: endStr,
      sortColumn: this.mapper.mapSortColumn(this.sortColumn),
      sortDirection: this.sortDirection || undefined
    }).subscribe({
      next: (res) => {

        // Map DTOs to models
        this.invoices = (res.data || []).map(dto => this.mapper.mapFullTaxFromDto(dto));
        this.totalItems = res.total || 0;
        this.totalPages = res.totalPages || 0;

        // Extract unique options if we have raw database list
        if (this.rawInvoicesDto.length === 0 && res.data) {
          this.rawInvoicesDto = res.data;
          this.extractFilterOptions(res.data);
        }

        this.lastFetchedTime = new Date();
        this.isLoading = false;
        console.log('Successfully loaded fulltax invoices list', res);
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to load fulltax report table data', err);
        this.toast.error('ไม่สามารถดึงข้อมูลรายการใบกำกับภาษีได้');
      }
    });
  }

  private resolveUserId(): number {
    return Number(this.cookieService.get('user_id')) || 0;
  }

  /**
   * Sends row invoice data to fulltax report API and loads the resulting PDF
   */
  sendToFullTaxReport(item: FullTaxReport) {
    this.isLoading = true;

    this.apiService.generateFullTaxReportPdf(item.id, item).subscribe({
      next: (blob: Blob) => {
        this.isLoading = false;
        this.toast.success('ทำรายงานสำเร็จ เรียบร้อยแล้ว!');

        // Convert the blob response into a safe resource URL for the iframe preview
        const pdfBlobUrl = URL.createObjectURL(blob);
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfBlobUrl);
        this.previewingInvoice = item;
        this.isPdfPreviewOpen = true;

        // Auto reload table to reflect status
        this.loadTableData();
        console.log('Successfully generated FullTax PDF Blob');
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to generate fulltax PDF report', err);
        this.toast.error('การส่งข้อมูลไปทำรายงาน Full Tax ล้มเหลว');
      }
    });
  }
}
